import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import {
  Plus, ClipboardList, Filter, X, Download, Trash2, Upload, Calendar as CalIcon,
  Building2, MapPin, FileText, Paperclip, RotateCcw, PlayCircle, CheckCircle2, XCircle,
  UserRound, UserCheck,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STATUSES = [
  { key: "solicitado", label: "Solicitado", color: "bg-neutral-100 text-neutral-700" },
  { key: "agendado", label: "Agendado", color: "bg-blue-50 text-blue-700" },
  { key: "em_execucao", label: "Em execução", color: "bg-amber-50 text-amber-700" },
  { key: "concluido", label: "Concluído", color: "bg-emerald-50 text-emerald-700" },
  { key: "cancelado", label: "Cancelado", color: "bg-red-50 text-red-700" },
];
const INSPECTION_TYPES = [
  { key: "entrada", label: "Entrada" },
  { key: "saida", label: "Saída" },
  { key: "conferencia", label: "Conferência" },
  { key: "laudo", label: "Laudo" },
];

const StatusBadge = ({ status }) => {
  const s = STATUSES.find((x) => x.key === status) || { label: status, color: "bg-neutral-100 text-neutral-700" };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${s.color}`}>{s.label}</span>;
};

const emptyForm = {
  agency_id: "", city_id: "", inspection_type: "entrada",
  address: { street: "", number: "", complement: "", neighborhood: "", zip: "" },
  property: { kind: "residencial", area_m2: 60, bedrooms: 2, furnished: false, notes: "" },
  tenant: { name: "", phone: "", email: "" },
  scheduled_date: "", scheduled_slots: [], notes: "",
};

export default function Requests() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [cities, setCities] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [view, setView] = useState("list"); // list|kanban
  const [showNew, setShowNew] = useState(false);
  const [detail, setDetail] = useState(null); // full request being viewed/edited

  const canCreate = !["vistoriador"].includes(user?.role);
  const isInspectorView = user?.role === "vistoriador";
  const canAssign = ["admin_geral", "administrativo", "supervisor"].includes(user?.role);

  // Auto-open create modal quando chega via ?new=1 (deep link do Portal)
  useEffect(() => {
    if (searchParams.get("new") === "1" && canCreate) {
      setShowNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, canCreate]);

  const load = useCallback(async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (cityFilter) params.city_id = cityFilter;
    if (agencyFilter) params.agency_id = agencyFilter;
    try {
      const { data } = await api.get("/requests", { params });
      setItems(data);
    } catch (e) { toast.error(extractError(e)); }
  }, [statusFilter, cityFilter, agencyFilter]);

  useEffect(() => {
    Promise.all([api.get("/agencies"), api.get("/cities")]).then(([a, c]) => {
      setAgencies(a.data); setCities(c.data);
    });
    if (["admin_geral", "administrativo", "supervisor"].includes(user?.role)) {
      api.get("/users").then((r) => {
        setInspectors(r.data.filter((u) => u.role === "vistoriador" && u.is_active));
      }).catch(() => {});
    }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (r) => {
    try {
      const { data } = await api.get(`/requests/${r.id}`);
      setDetail(data);
    } catch (e) { toast.error(extractError(e)); }
  };

  const groups = useMemo(() => {
    const g = {};
    STATUSES.forEach((s) => g[s.key] = []);
    items.forEach((r) => { (g[r.status] = g[r.status] || []).push(r); });
    return g;
  }, [items]);

  const cityName = (id) => { const c = cities.find((x) => x.id === id); return c ? `${c.name}/${c.state}` : "—"; };

  return (
    <div className="space-y-6" data-testid="requests-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">
            {isInspectorView ? "Fase 2 · Operação · Vistoriador" : "Fase 2 · Operação"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">
            {isInspectorView ? "Minhas Vistorias" : "Solicitações"}
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            {isInspectorView
              ? "Vistorias atribuídas a você. Anexe o laudo e conclua ao finalizar."
              : "Solicitar, agendar, acompanhar e cancelar vistorias."}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex border border-[#E5E7EB] rounded overflow-hidden">
            <button onClick={() => setView("list")} className={`px-3 py-1.5 text-xs ${view === "list" ? "bg-[#0B1F3A] text-white" : "bg-white text-[#0A0A0A]"}`} data-testid="requests-view-list">Lista</button>
            <button onClick={() => setView("kanban")} className={`px-3 py-1.5 text-xs ${view === "kanban" ? "bg-[#0B1F3A] text-white" : "bg-white text-[#0A0A0A]"}`} data-testid="requests-view-kanban">Kanban</button>
          </div>
          {canCreate && (
            <Button onClick={() => setShowNew(true)} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="requests-new-btn">
              <Plus size={16} className="mr-2" /> Nova solicitação
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <Filter size={14} className="text-[#6B7280]" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-[#E5E7EB] text-xs rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
          data-testid="requests-filter-status">
          <option value="">Todos os status</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
          className="bg-white border border-[#E5E7EB] text-xs rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
          data-testid="requests-filter-city">
          <option value="">Todas as cidades</option>
          {cities.map((c) => <option key={c.id} value={c.id}>{c.name}/{c.state}</option>)}
        </select>
        {["admin_geral","administrativo","financeiro","comercial","supervisor"].includes(user?.role) && (
          <select value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)}
            className="bg-white border border-[#E5E7EB] text-xs rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
            data-testid="requests-filter-agency">
            <option value="">Todas as imobiliárias</option>
            {agencies.map((a) => <option key={a.id} value={a.id}>{a.trade_name}</option>)}
          </select>
        )}
        {(statusFilter || cityFilter || agencyFilter) && (
          <button onClick={() => { setStatusFilter(""); setCityFilter(""); setAgencyFilter(""); }}
            className="text-xs text-[#6B7280] hover:text-[#0B1F3A] flex items-center gap-1" data-testid="requests-filter-clear">
            <X size={12} /> Limpar
          </button>
        )}
      </div>

      {view === "list" && (
        <div className="border border-[#E5E7EB] bg-white rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <th className="px-4 py-3 font-bold">Imobiliária / Endereço</th>
                <th className="px-4 py-3 font-bold">Cidade</th>
                <th className="px-4 py-3 font-bold">Data / Horário</th>
                <th className="px-4 py-3 font-bold">Tipo</th>
                <th className="px-4 py-3 font-bold">Vistoriador</th>
                <th className="px-4 py-3 font-bold text-right">Área</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold text-right">Anexos</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} onClick={() => openDetail(r)} className="border-b border-[#F1F2F4] hover:bg-[#F9FAFB] cursor-pointer" data-testid={`request-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold text-[#0A0A0A]">{r.agency_name}</div>
                    <div className="text-xs text-[#6B7280]">{r.address?.street}, {r.address?.number}{r.address?.neighborhood ? ` · ${r.address.neighborhood}` : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0A0A0A]">{r.city_name || cityName(r.city_id)}</td>
                  <td className="px-4 py-3 font-mono-data text-xs text-[#0A0A0A]">{r.scheduled_date} <span className="text-[#6B7280]">{(r.scheduled_slots || []).join(" + ")}</span></td>
                  <td className="px-4 py-3 text-xs capitalize">{r.inspection_type}</td>
                  <td className="px-4 py-3 text-xs text-[#0A0A0A]">
                    {r.assigned_inspector_name ? (
                      <span className="inline-flex items-center gap-1"><UserCheck size={12} className="text-emerald-600" /> {r.assigned_inspector_name}</span>
                    ) : (
                      <span className="text-[#9CA3AF]">— não atribuído —</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-data">{r.property?.area_m2} m²</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 text-right text-xs text-[#6B7280]">
                    {(r.attachments || []).length > 0 && <span className="inline-flex items-center gap-1"><Paperclip size={12} /> {r.attachments.length}</span>}
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-[#9CA3AF] text-sm">Nenhuma solicitação encontrada.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === "kanban" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="requests-kanban">
          {STATUSES.map((s) => (
            <div key={s.key} className="bg-white border border-[#E5E7EB] rounded-md">
              <div className={`px-3 py-2 border-b border-[#E5E7EB] flex items-center justify-between`}>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${s.color}`}>{s.label}</span>
                <span className="text-xs text-[#6B7280] font-mono-data">{(groups[s.key] || []).length}</span>
              </div>
              <div className="p-2 space-y-2 min-h-[80px] max-h-[560px] overflow-y-auto">
                {(groups[s.key] || []).map((r) => (
                  <div key={r.id} onClick={() => openDetail(r)} className="border border-[#E5E7EB] rounded p-2 hover:border-[#0B1F3A] cursor-pointer text-xs" data-testid={`kanban-card-${r.id}`}>
                    <div className="font-semibold text-[#0A0A0A] truncate">{r.agency_name}</div>
                    <div className="text-[#6B7280] truncate">{r.address?.street}, {r.address?.number}</div>
                    <div className="flex items-center gap-1 mt-1 text-[#0B1F3A] font-mono-data">
                      <CalIcon size={11} /> {r.scheduled_date} · {(r.scheduled_slots || []).join(",")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showNew && (
        <NewRequestDialog
          open={showNew} onClose={() => setShowNew(false)} onCreated={load}
          agencies={agencies} cities={cities} user={user}
        />
      )}

      {detail && (
        <RequestDetailDialog
          open={!!detail} data={detail} onClose={() => setDetail(null)} onChanged={(d) => { setDetail(d); load(); }}
          user={user} inspectors={inspectors} canAssign={canAssign}
        />
      )}
    </div>
  );
}

/* ---------- New Request Dialog ---------- */
function NewRequestDialog({ open, onClose, onCreated, agencies, cities, user }) {
  const [form, setForm] = useState({ ...emptyForm });
  const [availability, setAvailability] = useState(null); // {slots, closed, ...}
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setAddr = (patch) => setForm((f) => ({ ...f, address: { ...f.address, ...patch } }));
  const setProp = (patch) => setForm((f) => ({ ...f, property: { ...f.property, ...patch } }));
  const setTenant = (patch) => setForm((f) => ({ ...f, tenant: { ...f.tenant, ...patch } }));

  // If user is imobiliaria_*, prefill agency
  useEffect(() => {
    if (["imobiliaria_admin", "imobiliaria_user"].includes(user?.role) && user?.agency_id) {
      setF({ agency_id: user.agency_id });
    }
  }, [user]);

  const selectedAgency = agencies.find((a) => a.id === form.agency_id);
  const allowedCities = selectedAgency ? cities.filter((c) => (selectedAgency.city_ids || []).includes(c.id)) : [];

  // Fetch availability when city+date change
  useEffect(() => {
    setAvailability(null);
    setF({ scheduled_slots: [] });
    if (!form.city_id || !form.scheduled_date) return;
    api.get("/schedule/availability", { params: { city_id: form.city_id, date: form.scheduled_date } })
      .then((r) => setAvailability(r.data))
      .catch((e) => toast.error(extractError(e)));
  }, [form.city_id, form.scheduled_date]);

  const threshold = availability?.large_property_threshold ?? 130;
  const requires2 = Number(form.property.area_m2) > threshold;

  const toggleSlot = (t) => {
    setForm((f) => {
      const cur = f.scheduled_slots;
      if (cur.includes(t)) return { ...f, scheduled_slots: cur.filter((x) => x !== t) };
      const next = requires2 ? [...cur, t].slice(-2) : [t];
      return { ...f, scheduled_slots: next };
    });
  };

  const submit = async () => {
    if (!form.agency_id || !form.city_id) { toast.error("Selecione imobiliária e cidade"); return; }
    if (!form.scheduled_date || form.scheduled_slots.length === 0) { toast.error("Selecione data e horário"); return; }
    if (!form.address.street || !form.address.number) { toast.error("Preencha o endereço"); return; }
    setSaving(true);
    try {
      const payload = { ...form, property: { ...form.property, area_m2: Number(form.property.area_m2), bedrooms: Number(form.property.bedrooms) } };
      const { data } = await api.post("/requests", payload);
      // Upload attachments
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/requests/${data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch (e) { toast.error(`Falha no anexo ${f.name}: ${extractError(e)}`); }
      }
      toast.success("Solicitação criada");
      onCreated(); onClose();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-[#0B1F3A]">Nova solicitação de vistoria</DialogTitle>
          <DialogDescription className="text-[#6B7280]">Preencha os dados do imóvel, escolha um horário disponível na agenda e anexe documentos, se necessário.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Imobiliária</Label>
            <select value={form.agency_id} onChange={(e) => setF({ agency_id: e.target.value, city_id: "" })}
              disabled={["imobiliaria_admin", "imobiliaria_user"].includes(user?.role)}
              className="w-full bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
              data-testid="new-request-agency">
              <option value="">— selecione —</option>
              {agencies.map((a) => <option key={a.id} value={a.id}>{a.trade_name}</option>)}
            </select>
          </div>
          <div>
            <Label>Cidade</Label>
            <select value={form.city_id} onChange={(e) => setF({ city_id: e.target.value })}
              disabled={!form.agency_id}
              className="w-full bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
              data-testid="new-request-city">
              <option value="">— selecione —</option>
              {allowedCities.map((c) => <option key={c.id} value={c.id}>{c.name}/{c.state}</option>)}
            </select>
          </div>

          <div>
            <Label>Tipo de vistoria</Label>
            <select value={form.inspection_type} onChange={(e) => setF({ inspection_type: e.target.value })}
              className="w-full bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none"
              data-testid="new-request-type">
              {INSPECTION_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Tipo de imóvel</Label>
            <select value={form.property.kind} onChange={(e) => setProp({ kind: e.target.value })}
              className="w-full bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none">
              <option value="residencial">Residencial</option>
              <option value="comercial">Comercial</option>
            </select>
          </div>

          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><Label>Área (m²)</Label><Input type="number" value={form.property.area_m2} onChange={(e) => setProp({ area_m2: e.target.value })} data-testid="new-request-area" /></div>
            <div><Label>Quartos</Label><Input type="number" value={form.property.bedrooms} onChange={(e) => setProp({ bedrooms: e.target.value })} /></div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.property.furnished} onChange={(e) => setProp({ furnished: e.target.checked })} /> Mobiliado
              </label>
            </div>
            <div><Label>Data</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setF({ scheduled_date: e.target.value })} data-testid="new-request-date" /></div>
          </div>

          <div className="sm:col-span-2 border border-[#E5E7EB] rounded p-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="mb-0">Horários disponíveis {requires2 && <span className="text-[10px] text-[#0B1F3A] font-bold ml-2">(imóvel {'>'}{threshold}m² = 2 horários consecutivos)</span>}</Label>
              {availability && <span className="text-[10px] text-[#6B7280] font-mono-data">{availability.city_name} · {form.scheduled_date}</span>}
            </div>
            {!form.city_id || !form.scheduled_date ? (
              <div className="text-xs text-[#9CA3AF]">Selecione imobiliária, cidade e data para ver a disponibilidade.</div>
            ) : !availability ? (
              <div className="text-xs text-[#9CA3AF]">Carregando…</div>
            ) : availability.closed ? (
              <div className="text-xs text-red-600">Cidade não opera neste dia da semana.</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availability.slots.map((s) => {
                  const active = form.scheduled_slots.includes(s.time);
                  const disabled = !s.available;
                  return (
                    <button type="button" key={s.time} disabled={disabled} onClick={() => toggleSlot(s.time)}
                      className={`px-3 py-1.5 text-xs border rounded font-mono-data ${
                        disabled ? "bg-neutral-100 border-neutral-200 text-neutral-400 cursor-not-allowed" :
                        active ? "bg-[#0B1F3A] border-[#0B1F3A] text-[#FFD400] font-semibold" :
                        "bg-white border-[#E5E7EB] text-[#0A0A0A] hover:border-[#0B1F3A]"
                      }`}
                      data-testid={`slot-${s.time.replace(":","")}`}>
                      {s.time}{disabled && " · ocupado"}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="col-span-2"><Label>Rua</Label><Input value={form.address.street} onChange={(e) => setAddr({ street: e.target.value })} data-testid="new-request-street" /></div>
            <div><Label>Número</Label><Input value={form.address.number} onChange={(e) => setAddr({ number: e.target.value })} data-testid="new-request-number" /></div>
            <div><Label>Complemento</Label><Input value={form.address.complement} onChange={(e) => setAddr({ complement: e.target.value })} /></div>
            <div className="col-span-2"><Label>Bairro</Label><Input value={form.address.neighborhood} onChange={(e) => setAddr({ neighborhood: e.target.value })} /></div>
            <div className="col-span-2"><Label>CEP</Label><Input value={form.address.zip} onChange={(e) => setAddr({ zip: e.target.value })} /></div>
          </div>

          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div><Label>Inquilino</Label><Input value={form.tenant.name} onChange={(e) => setTenant({ name: e.target.value })} /></div>
            <div><Label>Telefone</Label><Input value={form.tenant.phone} onChange={(e) => setTenant({ phone: e.target.value })} /></div>
            <div><Label>E-mail</Label><Input value={form.tenant.email} onChange={(e) => setTenant({ email: e.target.value })} /></div>
          </div>

          <div className="sm:col-span-2"><Label>Observações</Label><Textarea value={form.notes} onChange={(e) => setF({ notes: e.target.value })} data-testid="new-request-notes" /></div>

          <div className="sm:col-span-2">
            <Label>Anexos (PDF, imagens, documentos — até 10 MB cada)</Label>
            <div className="flex items-center gap-2 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-white border border-[#E5E7EB] rounded text-sm hover:border-[#0B1F3A]" data-testid="new-request-file-label">
                <Upload size={14} /> Escolher arquivos
                <input type="file" multiple hidden onChange={(e) => setFiles(Array.from(e.target.files || []))} data-testid="new-request-file-input" />
              </label>
              {files.length > 0 && <span className="text-xs text-[#6B7280]">{files.length} arquivo(s) selecionado(s)</span>}
            </div>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-[#0A0A0A]">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between border border-[#E5E7EB] rounded px-2 py-1">
                    <span className="flex items-center gap-1"><FileText size={12} className="text-[#6B7280]" /> {f.name}</span>
                    <button onClick={() => setFiles((arr) => arr.filter((_, ix) => ix !== i))} className="text-[#6B7280] hover:text-red-600"><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="new-request-submit">{saving ? "Salvando…" : "Criar solicitação"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Detail Dialog ---------- */
function RequestDetailDialog({ open, data, onClose, onChanged, user, inspectors = [], canAssign = false }) {
  const [uploading, setUploading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const isInspector = user?.role === "vistoriador";
  const isStaff = ["admin_geral", "administrativo", "supervisor"].includes(user?.role);

  const canUpload = data.status !== "cancelado" && data.status !== "concluido";
  const canCancel = !isInspector && !["cancelado", "concluido"].includes(data.status);
  const canStart = (isStaff || isInspector) && data.status === "agendado";
  const canComplete = (isStaff || isInspector) && data.status === "em_execucao";

  const assignInspector = async (inspectorId) => {
    setAssigning(true);
    try {
      const { data: fresh } = await api.patch(`/requests/${data.id}`, { assigned_inspector_id: inspectorId || null });
      onChanged(fresh);
      toast.success(inspectorId ? "Vistoriador atribuído" : "Atribuição removida");
    } catch (e) { toast.error(extractError(e)); }
    finally { setAssigning(false); }
  };

  const doUpload = async (fileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/requests/${data.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const { data: fresh } = await api.get(`/requests/${data.id}`);
      onChanged(fresh);
      toast.success("Anexo(s) enviado(s)");
    } catch (e) { toast.error(extractError(e)); }
    finally { setUploading(false); }
  };

  const removeAtt = async (att) => {
    if (!window.confirm(`Remover ${att.filename}?`)) return;
    try {
      await api.delete(`/requests/${data.id}/attachments/${att.id}`);
      const { data: fresh } = await api.get(`/requests/${data.id}`);
      onChanged(fresh);
      toast.success("Anexo removido");
    } catch (e) { toast.error(extractError(e)); }
  };

  const downloadAtt = async (att) => {
    try {
      const resp = await api.get(`/requests/${data.id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "arquivo"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(extractError(e)); }
  };

  const setStatus = async (status) => {
    try {
      const { data: fresh } = await api.patch(`/requests/${data.id}`, { status });
      onChanged(fresh);
      toast.success("Status atualizado");
    } catch (e) { toast.error(extractError(e)); }
  };

  const submitCancel = async () => {
    setCancelling(true);
    try {
      const { data: fresh } = await api.post(`/requests/${data.id}/cancel`, { reason });
      onChanged(fresh); setShowCancel(false); setReason("");
      toast.success("Solicitação cancelada");
    } catch (e) { toast.error(extractError(e)); }
    finally { setCancelling(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="request-detail">
        <DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <DialogTitle className="font-display text-[#0B1F3A]">Solicitação</DialogTitle>
            <StatusBadge status={data.status} />
          </div>
          <DialogDescription className="text-[#6B7280]">
            {data.agency_name} · {data.city_name} · {data.scheduled_date} {data.scheduled_slots?.join(" + ")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="border border-[#E5E7EB] rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-2">Endereço</div>
            <div>{data.address?.street}, {data.address?.number}{data.address?.complement ? ` — ${data.address.complement}` : ""}</div>
            <div className="text-[#6B7280]">{data.address?.neighborhood} {data.address?.zip && `· ${data.address.zip}`}</div>
          </div>
          <div className="border border-[#E5E7EB] rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-2">Imóvel</div>
            <div className="capitalize">{data.property?.kind} · {data.property?.area_m2} m² · {data.property?.bedrooms} quartos {data.property?.furnished ? "· mobiliado" : ""}</div>
            {data.property?.notes && <div className="text-[#6B7280] mt-1">{data.property.notes}</div>}
          </div>
          <div className="border border-[#E5E7EB] rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-2">Inquilino</div>
            <div>{data.tenant?.name || "—"}</div>
            <div className="text-[#6B7280] text-xs">{data.tenant?.phone} {data.tenant?.email && `· ${data.tenant.email}`}</div>
          </div>
          <div className="border border-[#E5E7EB] rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-2">Detalhes</div>
            <div className="capitalize">Tipo: {data.inspection_type}</div>
            <div className="text-[#6B7280] text-xs">Criada em {new Date(data.created_at).toLocaleString("pt-BR")}</div>
            {data.notes && <div className="text-[#6B7280] mt-1 text-xs">{data.notes}</div>}
          </div>
        </div>

        {/* Assignment */}
        <div className="border border-[#E5E7EB] rounded p-3">
          <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-2">Vistoriador atribuído</div>
          {canAssign ? (
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={data.assigned_inspector_id || ""}
                onChange={(e) => assignInspector(e.target.value || null)}
                disabled={assigning || data.status === "cancelado" || data.status === "concluido"}
                className="bg-white border border-[#E5E7EB] text-sm rounded px-3 py-2 focus:border-[#0B1F3A] focus:outline-none min-w-[220px]"
                data-testid="detail-assign-inspector"
              >
                <option value="">— não atribuído —</option>
                {inspectors.map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
              {data.assigned_inspector_name && <span className="text-xs text-[#6B7280] flex items-center gap-1"><UserCheck size={12} className="text-emerald-600" /> ativo</span>}
            </div>
          ) : (
            <div className="text-sm">
              {data.assigned_inspector_name ? (
                <span className="inline-flex items-center gap-1 text-[#0A0A0A]"><UserRound size={14} className="text-[#0B1F3A]" /> {data.assigned_inspector_name}</span>
              ) : (
                <span className="text-[#9CA3AF]">Nenhum vistoriador atribuído ainda.</span>
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="border border-[#E5E7EB] rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">
              {isInspector ? "Laudo e anexos" : "Anexos"}
            </div>
            {canUpload && (
              <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-[#0B1F3A] font-semibold hover:underline" data-testid="detail-upload-label">
                <Upload size={12} /> {uploading ? "Enviando…" : (isInspector ? "Enviar laudo" : "Adicionar")}
                <input type="file" multiple hidden disabled={uploading} onChange={(e) => e.target.files && doUpload(e.target.files)} data-testid="detail-upload-input" />
              </label>
            )}
          </div>
          {isInspector && data.status === "em_execucao" && (data.attachments || []).length === 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
              Anexe o laudo (PDF ou fotos) antes de concluir esta vistoria.
            </div>
          )}
          {(data.attachments || []).length === 0 ? (
            <div className="text-xs text-[#9CA3AF]">Nenhum anexo.</div>
          ) : (
            <ul className="space-y-1">
              {data.attachments.map((a) => (
                <li key={a.id} className="flex items-center justify-between border border-[#E5E7EB] rounded px-2 py-1 text-xs" data-testid={`att-${a.id}`}>
                  <span className="flex items-center gap-2 truncate">
                    <FileText size={12} className="text-[#0B1F3A]" />
                    <span className="truncate">{a.filename}</span>
                    <span className="text-[#9CA3AF] font-mono-data">{(a.size / 1024).toFixed(0)} KB</span>
                  </span>
                  <span className="flex items-center gap-2">
                    <button onClick={() => downloadAtt(a)} className="text-[#6B7280] hover:text-[#0B1F3A]" title="Baixar" data-testid={`att-download-${a.id}`}><Download size={14} /></button>
                    {!isInspector && <button onClick={() => removeAtt(a)} className="text-[#6B7280] hover:text-red-600" title="Remover" data-testid={`att-delete-${a.id}`}><Trash2 size={14} /></button>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* History */}
        {(data.history || []).length > 0 && (
          <div className="border border-[#E5E7EB] rounded p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mb-2">Histórico</div>
            <ul className="space-y-1 text-xs">
              {data.history.map((h, i) => (
                <li key={i} className="flex items-center gap-2 text-[#0A0A0A]">
                  <span className="text-[#9CA3AF] font-mono-data">{new Date(h.at).toLocaleString("pt-BR")}</span>
                  <span className="text-[#6B7280]">{h.user_email}</span>
                  <span className="text-[#0B1F3A] font-semibold">{h.action}</span>
                  {h.from && <span className="text-[#6B7280]">{h.from} → {h.to}</span>}
                  {h.note && <span className="text-[#6B7280] italic">&ldquo;{h.note}&rdquo;</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {data.status === "agendado" && canStart && (
            <Button onClick={() => setStatus("em_execucao")} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="detail-start-btn">
              <PlayCircle size={14} className="mr-1" /> Iniciar
            </Button>
          )}
          {data.status === "em_execucao" && canComplete && (
            <Button onClick={() => setStatus("concluido")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="detail-complete-btn">
              <CheckCircle2 size={14} className="mr-1" /> Concluir
            </Button>
          )}
          {canCancel && (
            <Button onClick={() => setShowCancel(true)} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid="detail-cancel-btn">
              <XCircle size={14} className="mr-1" /> Cancelar
            </Button>
          )}
          <Button variant="outline" onClick={onClose} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Fechar</Button>
        </DialogFooter>

        {showCancel && (
          <Dialog open={showCancel} onOpenChange={(v) => !v && setShowCancel(false)}>
            <DialogContent className="bg-white max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-display text-[#0B1F3A]">Cancelar solicitação</DialogTitle>
                <DialogDescription className="text-[#6B7280]">Informe o motivo (opcional).</DialogDescription>
              </DialogHeader>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-input" />
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCancel(false)}>Voltar</Button>
                <Button onClick={submitCancel} disabled={cancelling} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cancel-confirm-btn">{cancelling ? "…" : "Confirmar"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
